<script setup lang="ts">
import { computed, ref, useId, watch } from 'vue'
import { formatMoneyInput, parseMoneyInput } from '../money'

// Amount in reais typed like a cash register: the digits fill from the cents ("12345" → 123,45).
// v-model is the number (null when empty).
const props = defineProps<{
  label: string
  hint?: string
  error?: string
  disabled?: boolean
  allowNegative?: boolean
  id?: string
}>()

const model = defineModel<number | null>({ default: null })

const generatedId = useId()
const inputId = computed(() => props.id ?? `money-${generatedId}`)
const text = ref(formatMoneyInput(model.value))
let negative = (model.value ?? 0) < 0

watch(model, (value) => {
  if (parseMoneyInput(text.value, negative) !== value) {
    negative = (value ?? 0) < 0
    text.value = formatMoneyInput(value)
  }
})

function onInput(event: Event) {
  const raw = (event.target as HTMLInputElement).value
  if (props.allowNegative) negative = raw.trim().startsWith('-')
  const value = parseMoneyInput(raw, negative)
  text.value = formatMoneyInput(value)
  ;(event.target as HTMLInputElement).value = text.value
  model.value = value
}
</script>

<template>
  <div class="ui-field">
    <label :for="inputId" class="ui-field__label">{{ label }}</label>
    <div class="ui-money" :class="{ 'ui-money--invalid': !!error, 'ui-money--disabled': disabled }">
      <span class="ui-money__prefix" aria-hidden="true">R$</span>
      <input
        :id="inputId"
        :value="text"
        class="ui-money__input"
        inputmode="numeric"
        autocomplete="off"
        :disabled="disabled"
        :aria-invalid="!!error || undefined"
        :aria-describedby="error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined"
        @input="onInput"
      />
    </div>
    <span v-if="error" :id="`${inputId}-error`" role="alert" class="ui-field__error">{{ error }}</span>
    <span v-if="hint" :id="`${inputId}-hint`" class="ui-field__hint">{{ hint }}</span>
  </div>
</template>

<style scoped>
.ui-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}
.ui-field__label {
  font-size: var(--type-body-size);
  font-weight: var(--weight-medium);
  color: var(--text-label);
}
.ui-money {
  display: flex;
  align-items: center;
  height: var(--field-height);
  border: 1px solid var(--opaque-separator);
  border-radius: var(--radius-field);
  background: var(--surface-card);
  padding: 0 12px;
  gap: 6px;
}
.ui-money:focus-within {
  border-color: var(--accent);
}
.ui-money--invalid {
  border-color: var(--error);
}
.ui-money--disabled {
  opacity: 0.6;
}
.ui-money__prefix {
  color: var(--text-secondary);
  font-size: var(--type-body-size);
}
.ui-money__input {
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  font-size: var(--type-body-size);
  color: var(--text-primary);
  text-align: right;
  outline: none;
  font-variant-numeric: tabular-nums;
}
.ui-field__error {
  font-size: var(--type-footnote-size);
  color: var(--error);
}
.ui-field__hint {
  font-size: var(--type-footnote-size);
  color: var(--text-secondary);
}
</style>
